import { computed } from "vue";
import layoutConfig from "@/configs/widgetsLayout";
import { WIDGET_SIGNATURES } from "../utils/widgets";

export function useWidgetLayout(widgetsRefOrArray) {
    const config = layoutConfig;

    const getWidgetList = () =>
        Array.isArray(widgetsRefOrArray?.value)
            ? widgetsRefOrArray.value
            : Array.isArray(widgetsRefOrArray)
                ? widgetsRefOrArray
                : [];

    const getCfg = (signature) => {
        const def = config.defaults || {};
        const wcfg = config.widgets?.[signature] || {};
        const list = getWidgetList();

        const hasOverviewBonus = list.some(
            (w) => w.signature === WIDGET_SIGNATURES.overview_bonus_credit_widget
        );

        const hasTireAchievement = list.some(
            (w) => w.signature === WIDGET_SIGNATURES.tire_achievement_goal_widget
        );

        const colsSource = (!hasOverviewBonus && !hasTireAchievement) && wcfg.colsIf ? wcfg.colsIf : wcfg.cols;
        const globalOrderIndex = config.order?.indexOf?.(signature) ?? -1;

        return {
            hidden: wcfg.hidden ?? def.hidden ?? false,
            order:
                (globalOrderIndex !== -1 ? globalOrderIndex : wcfg.order ?? def.order) ??
                1000,
            cols: {
                xl: colsSource?.xl ?? def.cols?.xl ?? 12,
                lg: colsSource?.lg ?? def.cols?.lg ?? 12,
                md: colsSource?.md ?? def.cols?.md ?? 12,
                sm: colsSource?.sm ?? def.cols?.sm ?? 12,
            },
            wrapInCard: wcfg.wrapInCard ?? def.wrapInCard ?? true,
        };
    };

    const visibleWidgets = computed(() => {
        const list = getWidgetList();

        const find = (sig) => list.find((w) => w.signature === sig);
        const tireGoal = find(WIDGET_SIGNATURES.tire_achievement_goal_widget);
        const points = find(WIDGET_SIGNATURES.find_more_about_points_widget);
        const brand = find(WIDGET_SIGNATURES.brand_widget);
        const rim = find(WIDGET_SIGNATURES.rim_widget);
        const season = find(WIDGET_SIGNATURES.season_widget);
        const business = find(WIDGET_SIGNATURES.business_unit_widget);

        let rest = list.filter(
            (w) =>
                ![
                    WIDGET_SIGNATURES.tire_achievement_goal_widget,
                    WIDGET_SIGNATURES.find_more_about_points_widget,
                    WIDGET_SIGNATURES.brand_widget,
                    WIDGET_SIGNATURES.rim_widget,
                    WIDGET_SIGNATURES.season_widget,
                    WIDGET_SIGNATURES.business_unit_widget,
                ].includes(w.signature)
        );

        if (tireGoal && points) {
            rest.unshift({
                signature: WIDGET_SIGNATURES.dealer_combined_achievement_goals_widget,
                data: {
                    tire_achievement_goal: tireGoal.data,
                    points: points.data,
                    brand: brand?.data,
                    rim: rim?.data,
                },
            });
        } else if (brand || rim) {
            rest.unshift({
                signature: WIDGET_SIGNATURES.brand_rim_widget,
                data: { brand: brand?.data, rim: rim?.data },
            });
        }

        if (season && business) {
            rest.unshift({
                signature: WIDGET_SIGNATURES.season_business_units_widget,
                data: { business_units: business.data, season: season.data },
            });
        }

        return rest
            .map((w) => ({ ...w, __layout: getCfg(w.signature) }))
            .filter((w) => !w.__layout.hidden)
            .sort((a, b) => a.__layout.order - b.__layout.order);
    });

    const getColsAttrs = (w) => {
        const cols = w.__layout?.cols || getCfg(w.signature).cols;
        const toStr = (v) => (v != null ? String(v) : undefined);
        return {
            cols: 12,
            xl: toStr(cols.xl),
            lg: toStr(cols.lg),
            md: toStr(cols.md),
            sm: toStr(cols.sm),
        };
    };

    return { visibleWidgets, getColsAttrs };
}
